import {
  AlertTriangle,
  Leaf,
  ChefHat,
  Truck,
  Warehouse,
  ArrowRightLeft,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { CatalogItem } from '../../../types';
import {
  ALLERGEN_LABELS,
  DIETARY_FLAG_LABELS,
} from '../../../types';

interface OverviewTabProps {readonly item: CatalogItem;}

function formatUnitDisplay(unit: string, unitSize?: number): string {
  if (unitSize && unitSize > 1) {
    return `${unit} (${unitSize})`;
  }
  return unit;
}

export function OverviewTab({ item }: OverviewTabProps) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900">Product Details</h3>
        <div className="rounded-lg border p-4 space-y-3">
          <InfoRow label="Category" value={item.category} />
          {item.subcategory && <InfoRow label="Subcategory" value={item.subcategory} />}
          <InfoRow label="Unit" value={formatUnitDisplay(item.unit, item.unitSize)} />
          {item.unitCost !== undefined && (
            <InfoRow label="Unit Cost" value={`$${item.unitCost.toFixed(2)}`} />
          )}
          {item.shelfLifeDays && <InfoRow label="Shelf Life" value={`${item.shelfLifeDays} days`} />}
          <InfoRow label="Status" value={item.isActive ? 'Active' : 'Inactive'} />
        </div>

        {/* Product Codes */}
        <h3 className="font-medium text-gray-900">Product Codes</h3>
        <div className="rounded-lg border p-4 space-y-3">
          <InfoRow label="SKU" value={item.sku} mono />
          {item.internalCode && <InfoRow label="Internal Code" value={item.internalCode} mono />}
          {item.ean && <InfoRow label="EAN" value={item.ean} mono />}
          {item.upc && <InfoRow label="UPC" value={item.upc} mono />}
          {item.gtin && <InfoRow label="GTIN" value={item.gtin} mono />}
        </div>

        {item.description && (
          <>
            <h3 className="font-medium text-gray-900">Description</h3>
            <p className="text-sm text-gray-600 rounded-lg border p-4">{item.description}</p>
          </>
        )}
      </div>

      {/* Allergens & Dietary */}
      <div className="space-y-4">
        {item.allergens.length > 0 && (
          <>
            <h3 className="flex items-center gap-2 font-medium text-gray-900">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Allergens
            </h3>
            <div className="flex flex-wrap gap-2">
              {item.allergens.map((allergen) => (
                <span
                  key={allergen}
                  className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700"
                >
                  {ALLERGEN_LABELS[allergen] ?? allergen}
                </span>
              ))}
            </div>
          </>
        )}

        {item.dietaryFlags.length > 0 && (
          <>
            <h3 className="flex items-center gap-2 font-medium text-gray-900">
              <Leaf className="h-4 w-4 text-green-500" />
              Dietary Information
            </h3>
            <div className="flex flex-wrap gap-2">
              {item.dietaryFlags.map((flag) => (
                <span
                  key={flag}
                  className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700"
                >
                  {DIETARY_FLAG_LABELS[flag] ?? flag}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Quick Stats */}
        <h3 className="font-medium text-gray-900">Quick Stats</h3>
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="Used in Recipes"
            value={item.recipeUsage?.length ?? 0}
            icon={ChefHat}
          />
          <StatCard
            label="Active Suppliers"
            value={item.suppliers?.length ?? 0}
            icon={Truck}
          />
          <StatCard
            label="Stock Locations"
            value={item.stockLevels?.length ?? 0}
            icon={Warehouse}
          />
          <StatCard
            label="Substitutes"
            value={item.relatedItems?.filter((r) => r.relationshipType === 'substitute').length ?? 0}
            icon={ArrowRightLeft}
          />
        </div>
      </div>
    </div>
  );
}

interface InfoRowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function InfoRow({
  label, value, mono 
}: InfoRowProps) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={cn('font-medium text-gray-900', mono && 'font-mono')}>{value}</span>
    </div>
  );
}

interface StatCardProps {
  readonly label: string;
  readonly value: number;
  readonly icon: typeof Package;
}

function StatCard({
  label, value, icon: statIcon 
}: StatCardProps) {
  const StatIcon = statIcon;
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-2 text-gray-500">
        <StatIcon className="h-4 w-4" />
        <span className="text-xs">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
    </div>
  );
}
